import * as Sentry from '@sentry/nextjs';

export const CONTACT_APP = 'ka-webapp';

/** @typedef {'popup' | 'contact_page'} ContactFormSurface */

/**
 * @typedef {Object} ContactSubmitMeta
 * @property {ContactFormSurface} surface
 * @property {string} [pathname]
 * @property {{ id?: string|number, email?: string, firstName?: string, lastName?: string }} [loggedInUser]
 */

/**
 * @typedef {import('./contactService').ContactFormData} ContactFormData
 */

function buildFormExtras(data) {
  return {
    submissionType: data.type,
    name: data.name,
    email: data.email,
    inquiryType: data.inquiryType,
    subject: data.subject,
    message: data.message,
  };
}

function applyContactScope(scope, options) {
  scope.setTag('app', CONTACT_APP);
  scope.setTag('contact.layer', options.layer);
  if (options.surface) scope.setTag('contact.surface', options.surface);
  if (options.submissionType) scope.setTag('contact.submission_type', options.submissionType);
  if (options.statusCode !== undefined) {
    scope.setTag('http.status_code', String(options.statusCode));
  }

  if (options.loggedInUser?.id || options.loggedInUser?.email) {
    scope.setUser({
      id:
        options.loggedInUser.id != null ? String(options.loggedInUser.id) : undefined,
      email: options.loggedInUser.email,
      username:
        [options.loggedInUser.firstName, options.loggedInUser.lastName]
          .filter(Boolean)
          .join(' ')
          .trim() || undefined,
    });
  }

  scope.setContext('contact_form', {
    app: CONTACT_APP,
    surface: options.surface ?? 'unknown',
    pathname: options.pathname,
    referer: options.referer,
    ...(options.formExtras ?? {}),
    apiMessage: options.apiMessage,
    statusCode: options.statusCode,
  });

  if (options.apiResponse !== undefined) {
    scope.setContext('api_response', {
      status: options.statusCode,
      body: options.apiResponse,
    });
  }
}

/**
 * @param {unknown} error
 * @param {ContactSubmitMeta} meta
 * @param {ContactFormData} formData
 * @param {{ httpStatus?: number, apiResponse?: unknown }} [details]
 */
export function captureContactFormClientFailure(error, meta, formData, details) {
  const err = error instanceof Error ? error : new Error(String(error));
  const apiMessage =
    details?.apiResponse &&
    typeof details.apiResponse === 'object' &&
    details.apiResponse !== null &&
    'message' in details.apiResponse &&
    typeof details.apiResponse.message === 'string'
      ? details.apiResponse.message
      : err.message;

  Sentry.withScope((scope) => {
    applyContactScope(scope, {
      layer: 'client',
      surface: meta.surface,
      pathname: meta.pathname,
      submissionType: formData.type,
      loggedInUser: meta.loggedInUser,
      formExtras: buildFormExtras(formData),
      statusCode: details?.httpStatus,
      apiMessage,
      apiResponse: details?.apiResponse,
    });
    Sentry.captureException(err);
  });
}

/**
 * @param {{
 *   statusCode: number,
 *   reason: string,
 *   formPayload: Record<string, unknown>,
 *   referer?: string|null,
 *   zendeskStatus?: number,
 *   zendeskBody?: string,
 *   missingEnvVar?: string,
 * }} context
 */
export function captureContactFormApiFailure(context) {
  Sentry.withScope((scope) => {
    applyContactScope(scope, {
      layer: 'api',
      referer: context.referer,
      submissionType:
        typeof context.formPayload.type === 'string' ? context.formPayload.type : undefined,
      formExtras: context.formPayload,
      statusCode: context.statusCode,
      apiMessage: context.reason,
    });
    scope.setContext('zendesk', {
      status: context.zendeskStatus,
      body: context.zendeskBody,
      missingEnvVar: context.missingEnvVar,
    });
    Sentry.captureMessage(`Contact form API failure: ${context.reason}`, 'error');
  });
}

/**
 * @param {{ id?: string|number, email?: string, firstName?: string, lastName?: string } | null | undefined} user
 */
export function mapLoggedInUserForContact(user) {
  if (!user) return undefined;
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
  };
}
